'use client';

import React, { useState, useEffect } from 'react';
import { CheckCircle, Phone, Mail, Calendar, Clock, Users, Video, Check } from 'lucide-react';
import { getNextWebinar, saveWebinarRegistration } from '@/lib/webinarStore';

interface Props {
  firstName: string;
  email: string;
  phone: string;
  onClose: () => void;
}

export default function OrderSuccessModal({ firstName, email, phone, onClose }: Props) {
  const [webinarReserved, setWebinarReserved] = useState(false);
  const [webinarInfo, setWebinarInfo] = useState({ date: '', time: '', link: '' });
  const modalRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setWebinarInfo(getNextWebinar());
  }, []);

  useEffect(() => {
    // Scroll the modal into view so it's visible at the user's current position on mobile
    if (modalRef.current) {
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleReserveSpot = () => {
    if (webinarReserved) return;
    saveWebinarRegistration({
      firstName,
      lastName: '',
      email,
      phone,
      webinarDate: webinarInfo.date,
      webinarTime: webinarInfo.time,
      webinarLink: webinarInfo.link,
    });
    console.log('=== WEBINAR CONFIRMATION SENT ===');
    console.log(`SMS to ${phone}: You're registered for the X39 Live Training on ${webinarInfo.date} at ${webinarInfo.time}. Join here: ${webinarInfo.link}`);
    console.log(`Email to ${email}: Webinar confirmation — ${webinarInfo.date} at ${webinarInfo.time}. Link: ${webinarInfo.link}`);

    // Send separate notification email to admin
    fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'webinar_signup',
        data: {
          firstName,
          lastName: '',
          email,
          phone,
          webinarDate: webinarInfo.date,
          webinarTime: webinarInfo.time,
        },
      }),
    }).catch((err) => console.error('Webinar notification error:', err));

    setWebinarReserved(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative z-10 w-full max-w-md rounded-2xl border"
        style={{
          background: 'linear-gradient(135deg, #0d1526 0%, #0a0e1a 100%)',
          borderColor: 'rgba(0,168,255,0.3)',
          boxShadow: '0 0 60px rgba(0,168,255,0.25)',
        }}
      >
        {/* Top section */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="relative w-14 h-14 mx-auto mb-3">
            <div className="absolute inset-0 rounded-full bg-green-500/10 border border-green-500/30 animate-ping" />
            <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center relative z-10">
              <CheckCircle size={28} className="text-green-400" />
            </div>
          </div>

          <h2 className="text-foreground font-extrabold text-2xl mb-1">🎉 Order Received!</h2>
          <p className="text-secondary-foreground text-sm leading-snug mb-4">
            Thank you, <span className="text-primary font-bold">{firstName}</span>! We will reach out shortly to confirm your order and collect payment.
          </p>

          <div className="glass-card p-3 text-left space-y-2">
            <div className="flex items-center gap-2 text-xs text-secondary-foreground">
              <Phone size={12} className="text-primary flex-shrink-0" />
              <span>Expect a call or text within 24–48 hours</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-secondary-foreground">
              <Mail size={12} className="text-primary flex-shrink-0" />
              <span>Check your email for a confirmation summary</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t mx-6" style={{ borderColor: 'rgba(0,168,255,0.2)' }} />

        {/* Webinar CTA Bubble */}
        <div className="p-4">
          <div
            className="rounded-2xl p-4 border-2 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(0,102,204,0.18) 0%, rgba(0,168,255,0.12) 100%)',
              borderColor: 'rgba(0,168,255,0.6)',
              boxShadow: '0 0 32px rgba(0,168,255,0.22), inset 0 0 24px rgba(0,168,255,0.06)',
            }}
          >
            <div
              className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(0,168,255,0.8), transparent)' }}
            />

            <div className="flex justify-center mb-2">
              <span
                className="text-xs font-bold uppercase tracking-widest px-3 py-0.5 rounded-full"
                style={{ background: 'rgba(0,168,255,0.18)', color: '#00a8ff', border: '1px solid rgba(0,168,255,0.4)' }}
              >
                ✦ Your Next Step
              </span>
            </div>

            <div className="text-center mb-3">
              <h3 className="text-foreground font-extrabold text-lg mb-0.5 leading-tight">
                <span className="gradient-text">Live Training</span>{' — '}Join Our Next Live Presentation
              </h3>
              <p className="text-secondary-foreground text-xs leading-snug">
                See the science live, hear real success stories, and experience the X39 opportunity firsthand.
              </p>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Video size={14} className="text-primary" />
              </div>
              <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <Calendar size={11} className="text-primary" />
                  <span className="text-muted-foreground">Next Session:</span>
                  <span className="text-foreground font-semibold">TBD</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={11} className="text-primary" />
                  <span className="text-foreground font-semibold">30 Min</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users size={11} className="text-primary" />
                  <span className="text-foreground font-semibold">Live</span>
                </div>
              </div>
            </div>

            {webinarReserved ? (
              <div
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
                style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80' }}
              >
                <Check size={14} />
                You&apos;re Registered! Confirmation sent to {email || 'your email'} &amp; {phone || 'your phone'}
              </div>
            ) : (
              <button
                onClick={handleReserveSpot}
                className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 font-bold text-sm"
                style={{ boxShadow: '0 0 20px rgba(0,168,255,0.4)' }}
              >
                <Calendar size={16} />
                Reserve My Spot — Join Next Session
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}